import { useBillingQueryContext } from 'modules/dataWarehouse/contexts/BillingQueryContext';
import { ModalAction, Source, Status } from 'modules/dataWarehouse/types';
import { filterSyncs, shouldRestrictCreation } from 'modules/dataWarehouse/utils';
import { BUTTON_SIZES, SecondaryButton } from 'ui/button';
import { Add16, DatabaseRetain } from 'ui/icons';
import { useModalContext } from 'utils/contexts/modal';
import { useGroupSelection } from 'utils/hooks/useGroupSelection';
import { SOURCES_LIST } from '../../constants';
import { useFiltersContext } from '../../contexts';
import { SourceCard } from '../SourceCard';
import { SyncsTable } from '../SyncsTable';

export const SOURCE_LIST_TEXT = {
  emptyStateSubtitle: 'Get started by connecting a new data source.',
  emptyStateTitle: 'No data syncs yet',
};

type SourcesProps = {
  sources: Source[];
};

/**
 * Sources component
 * Split from SourcesList in order to properly instanciate the selected values
 * used in `useGroupSelection` hook
 * This alleviated some issues with initial state of open cards and removes an
 * animation that was causing a flicker on the cards
 */
export const Sources = ({ sources }: SourcesProps) => {
  const defaultSelected = sources.map((source) => source.sourceId);
  const { debouncedSearch, status } = useFiltersContext();
  const { selected, toggleSelection } = useGroupSelection(defaultSelected);
  const { showModal } = useModalContext();
  const { currentProjectStatus, remainingSyncs } = useBillingQueryContext();

  return (
    <>
      {sources.map((source) => {
        const { type = '' } = source;

        // Pull icon from source list based on type, and fallback
        // to a default object in case there is a mismatch in the `id` provided
        // from the `allowedSourceTypes` endpoint and the object it maps to
        const { Icon, name } = SOURCES_LIST[type] ?? { Icon: DatabaseRetain, name: '' };

        const filteredSyncs = filterSyncs(source.syncs, debouncedSearch, status);

        const handleEdit = () =>
          showModal({ action: ModalAction.EDIT_SOURCE, payload: { id: source.sourceId } });

        const handleAdd = () => {
          const shouldRestrict = shouldRestrictCreation(currentProjectStatus, remainingSyncs);
          const action = shouldRestrict ? ModalAction.RESTRICT_CREATE : ModalAction.CREATE_SYNC;
          showModal({ action, payload: { sourceId: source.sourceId } });
        };

        return (
          <SourceCard
            key={source.sourceId}
            data-test="source-card"
            Actions={
              <>
                <SecondaryButton onClick={handleEdit} size={BUTTON_SIZES.SMALL}>
                  <span>Edit</span>
                </SecondaryButton>
                <SecondaryButton onClick={handleAdd} size={BUTTON_SIZES.SMALL}>
                  <Add16 />
                  <span>New sync</span>
                </SecondaryButton>
              </>
            }
            Icon={Icon}
            isOpen={selected.includes(source.sourceId)}
            name={source.name}
            setIsOpen={() => toggleSelection(source.sourceId)}
            syncsCount={{
              total: source.syncs.length,
              errors: source.syncs.filter((sync) =>
                [Status.Failed, Status.Warning].includes(sync.status),
              ).length,
            }}
            type={name}
          >
            <SyncsTable
              data-test="syncs-table"
              searchTerm={debouncedSearch}
              sourceId={source.sourceId}
              syncs={filteredSyncs}
              totalSyncs={source.syncs.length}
            />
          </SourceCard>
        );
      })}
    </>
  );
};
